import { Engagement, EngagementStatus } from "@wingspanhq/wingspan-api-sdk";
import { engagementsApi } from "@api";
import { queriesEngagements } from "@query";

/** Name given to a default engagement this app has to create itself. */
export const DEFAULT_ENGAGEMENT_NAME = "Default Engagement";

/**
 * Resolves the account's default engagement template, creating one if the
 * account has none.
 *
 * v1 kept a default engagement per account and silently attached to it every
 * relationship created without an explicit engagement. v3 has no such implicit
 * step — an engagement instance always names the template it comes from — so the
 * frontend resolves the template itself. Accounts that existed on v1 already
 * carry the default (`isDefault: true`), so the create branch is only for
 * accounts that never had one.
 *
 * The `isDefault` filter is honored server-side (live-probed on integration
 * 2026-07-28), and the flag is re-checked here so a deployment that ignores the
 * filter still resolves the right template rather than claiming the first
 * engagement it sees.
 *
 * Uses `forceFetchQueryData`, not the cache-first `fetchQueryData`: cache-first
 * is presence-based, so an empty list cached earlier in the session would make
 * this create a second default.
 */
export const ensureDefaultEngagement = async (): Promise<Engagement> => {
  const engagements =
    await queriesEngagements.listEngagements.forceFetchQueryData({
      filter: {
        isDefault: { eq: true },
        status: { anyOf: [EngagementStatus.ACTIVE] }
      },
      page: { size: 100 }
    });

  const existingDefault = (engagements.data ?? []).find(
    engagement => engagement.isDefault && engagement.id
  );

  if (existingDefault) {
    return existingDefault;
  }

  return engagementsApi.createEngagement({
    engagementCreate: { name: DEFAULT_ENGAGEMENT_NAME, isDefault: true }
  });
};
